import React, { useState, useEffect } from "react";
import Pagination from "@/components/pagination";
import SearchInput from "@/components/search-input";
import API from "@/services/api";
import toast from "react-hot-toast";
import UserModal from "../components/UserModal";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    total: "Total",
    no_candidates: "No candidates found",
    table: {
      name: "Name",
      email: "Email",
      phone: "Phone",
      organization: "Organization",
      created_date: "Created Date",
      last_login: "Last Login",
    },
  },
  fr: {
    total: "Total",
    no_candidates: "Aucun candidat trouvé",
    table: {
      name: "Nom",
      email: "Email",
      phone: "Téléphone",
      organization: "Organisation",
      created_date: "Date de création",
      last_login: "Dernière connexion",
    },
  },
};

const Candidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const limit = 25;
  const { t } = useComponentTranslation(translations);

  useEffect(() => {
    fetchCandidates();
  }, [currentPage, searchTerm, selectedOrganization]);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await API.post("/user/search", {
        page: currentPage,
        limit: limit,
        role_candidate: true,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedOrganization && { organization_id: selectedOrganization }),
      });

      if (response.ok) {
        setCandidates(response.data || []);
        setTotal(response.total || 0);
      } else {
        toast.error(response.code || "Error fetching candidates");
      }
    } catch (e) {
      console.error("Error fetching candidates:", e);
      toast.error(e.code || e.message || "Error fetching candidates");
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
  };

  return (
    <div className="p-6">
      {/* Search and Organization Filter */}
      <div className="mb-3 flex gap-3">
        <SearchInput
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          className="w-64"
        />
        <OrganizationSelect
          value={selectedOrganization}
          onChange={(value) => {
            setSelectedOrganization(value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Total and Pagination */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          {t("total")}: <span className="font-semibold text-gray-900">{total}</span>
        </div>
        <Pagination total={total} limit={limit} currentPage={currentPage} onPageChange={setCurrentPage} />
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : candidates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t("no_candidates")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">{t("table.name")}</th>
                  <th className="px-4 py-3">{t("table.email")}</th>
                  <th className="px-4 py-3">{t("table.phone")}</th>
                  <th className="px-4 py-3">{t("table.organization")}</th>
                  <th className="px-4 py-3">{t("table.created_date")}</th>
                  <th className="px-4 py-3">{t("table.last_login")}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {candidates.map((user) => {
                  const userId = user._id || user.id;
                  return (
                    <tr key={userId} onClick={() => handleViewUser(user)} className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {user.firstname || user.lastname ? `${user.firstname || ""} ${user.lastname || ""}`.trim() : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">{user.email || "-"}</span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{user.phone || "-"}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{user.organisations?.[0]?.name || "-"}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{user.created_at ? new Date(user.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{user.last_login_at ? new Date(user.last_login_at).toLocaleString("fr-FR") : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UserModal isOpen={!!selectedUser} onClose={() => setSelectedUser(null)} user={selectedUser} fetchData={fetchCandidates} />
    </div>
  );
};

// Organization Select Component
const OrganizationSelect = ({ value, onChange }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await API.post("/organization/search", { approved: true });

      if (response.ok) {
        setOrganizations(response.data || []);
      } else {
        toast.error("Failed to load organizations");
      }
    } catch (e) {
      console.error("Error fetching organizations:", e);
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-w-[250px]"
        disabled={loading}
      >
        <option value="">All Organizations</option>
        {organizations.map((org) => (
          <option key={org._id} value={org._id}>
            {org.name}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

export default Candidates;
